import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Screen from '../components/ui/Screen'
import Panel from '../components/ui/Panel'
import Input from '../components/ui/Input'
import Button from '../components/ui/Button'
import texts from '../texts'
import { getInvite, isLoggedIn as checkIsLoggedIn, joinClub, setAuthMarker } from '../lib/api'
import type { InviteInfo } from '../lib/api'

const PENDING_INVITE_KEY = 'wb-pending-invite'

interface JoinProps {
  setAuth: (value: boolean) => void
}

function Join({ setAuth }: JoinProps) {
  const { code } = useParams<{ code: string }>()
  const navigate = useNavigate()
  const [invite, setInvite] = useState<InviteInfo | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [loading, setLoading] = useState(true)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const isLoggedIn = checkIsLoggedIn()

  useEffect(() => {
    if (!code) return
    getInvite(code)
      .then(setInvite)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false))
  }, [code])

  const handleJoinLoggedIn = async () => {
    if (!code) return
    setError('')
    setBusy(true)
    try {
      const response = await joinClub(code)
      navigate(`/klub/${response.clubId}`)
    } catch (err: any) {
      setError(err.response?.data?.message || texts.join.genericJoinError)
    } finally {
      setBusy(false)
    }
  }

  const handleJoinAsGuest = async (e: FormEvent) => {
    e.preventDefault()
    if (!code) return
    const trimmed = name.trim()
    if (!trimmed) {
      setError(texts.join.missingName)
      return
    }
    setError('')
    setBusy(true)
    try {
      const response = await joinClub(code, trimmed)
      if (response.userId && response.name) {
        setAuthMarker(response.userId, response.name)
        setAuth(true)
      }
      navigate('/')
    } catch (err: any) {
      setError(err.response?.data?.message || texts.join.genericJoinError)
    } finally {
      setBusy(false)
    }
  }

  const handleGoToLogin = () => {
    if (code) localStorage.setItem(PENDING_INVITE_KEY, code)
    navigate('/login')
  }

  if (loading) {
    return (
      <Screen bare center>
        <p className="text-center text-text-2">{texts.join.loading}</p>
      </Screen>
    )
  }

  if (notFound || !invite) {
    return (
      <Screen bare center>
        <div className="grid gap-3.5 text-center">
          <h1>{texts.join.notFoundTitle}</h1>
          <Link to="/" className="text-text-2">
            ← WhistBuddy
          </Link>
        </div>
      </Screen>
    )
  }

  return (
    <Screen bare center>
      <div className="grid gap-3.5 text-center">
        <h1>{texts.join.invitedTo(invite.clubName)}</h1>
        <p className="text-text-2">{texts.join.members(invite.memberCount)}</p>
      </div>

      {isLoggedIn ? (
        <Button full onClick={handleJoinLoggedIn} disabled={busy}>
          {busy ? texts.join.joining : texts.join.joinButton}
        </Button>
      ) : (
        <Panel>
          <form className="grid gap-3" onSubmit={handleJoinAsGuest}>
            <Input
              placeholder={texts.join.namePlaceholder}
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Button type="submit" full disabled={busy}>
              {busy ? texts.join.joining : texts.join.createAndJoin}
            </Button>
          </form>
          <p className="text-center text-[0.8em] text-text-2">{texts.join.noPasswordHint}</p>
          <button type="button" className="text-center text-[0.85em] text-text-2" onClick={handleGoToLogin}>
            {texts.join.haveAccount}
          </button>
        </Panel>
      )}

      {error && <p className="text-center text-red">{error}</p>}
    </Screen>
  )
}

export default Join
